import {FaChevronRight} from 'react-icons/fa';
import {LuImageOff} from 'react-icons/lu';
import Link from 'next/link';

import type {LinkListSelectionFragment} from '@/components/content-blocks/link-list-selection/query';
import ImageFill from '@/modules/foundation/components/image/ImageFill';
import {Heading} from '@/modules/foundation/components/typography/heading';
import {Paragraph} from '@/modules/foundation/components/typography/paragraph';
import {imageWebFromImageWebSchema} from '@/modules/foundation/sanity/image/query';
import {linkFragmentToHref} from '@/modules/foundation/sanity/link/query';
import {cn} from '@/modules/foundation/shared/tailwind';

type Props = {
	className?: string;
	linkItem: NonNullable<LinkListSelectionFragment['linkItems']>[number];
	headingLevel?: 2 | 3;
};

export const LinkImageCard = ({className, linkItem, headingLevel = 2}: Props) => {
	const image = linkItem.image?.asset?._ref ? imageWebFromImageWebSchema(linkItem.image) : null;
	return (
		<Link
			href={linkItem.link ? linkFragmentToHref(linkItem.link) : '#'}
			{...(linkItem.link.targetBlank ? {target: '_blank'} : {})}
			className={cn(
				'flex',
				'flex-col',
				'items-stretch',
				'justify-end',
				'aspect-video',
				'bg-white',
				'overflow-hidden',
				'relative',
				'shadow-[0px_3px_3px_-3px_rgba(0,_0,_0,_0.1),_0px_1px_5px_rgba(0,_0,_0,_0.15)]',
				'hover:shadow-[0px_3px_3px_-2px_rgba(0,_0,_0,_0.16),_0px_0px_5px_2px_rgba(0,_0,_0,_0.08)]',
				className,
			)}
		>
			{image ? (
				<ImageFill
					src={image?.image.src}
					width={image.image.width}
					height={image.image.height}
					hotspot={image.image.hotspot}
					alt={linkItem.image?.altText ?? ''}
				/>
			) : (
				<LuImageOff
					aria-hidden
					className={cn('text-dypgronn-industri', 'text-[3rem]', 'mb-8', 'mx-auto')}
				/>
			)}

			<div
				className={cn(
					'z-0',
					'pt-8',
					'bg-[linear-gradient(180deg,_rgba(0,_12,_9,_0)_0%,_rgba(0,_12,_9,_0.75)_100%)]',
				)}
			>
				<div className={cn('my-6', 'mx-4')}>
					{linkItem.title && (
						<Heading
							level={headingLevel}
							className={cn(
								'text-xl!',
								'sm:text-2xl!',
								'text-white!',
								'font-medium!',
								'text-shadow-[0px_0px_5px_rgba(0,_0,_0,_0.75)]',
								'flex',
								'items-center',
								'gap-1',
							)}
						>
							{linkItem.title}
							<FaChevronRight aria-hidden className={cn('text-[1.2rem]')} />
						</Heading>
					)}
					{linkItem.intro && (
						<Paragraph
							className={cn(
								'text-white!',
								'text-shadow-[0px_0px_5px_rgba(0,_0,_0,_0.75)]',
							)}
						>
							{linkItem.intro}
						</Paragraph>
					)}
				</div>
			</div>
		</Link>
	);
};
